import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from '@mui/material';
import { useRef, useState } from 'react';
import { useNotify, useRefresh, useTranslate } from 'react-admin';
import { uploadCoinImage } from '../../../services/coinImagesService';
import { PermissionButton } from 'src/routes/components/permission-button';

type Props = {
  open: boolean;
  onClose: () => void;
  defaultCustomFilename?: string;
};

export default function CoinImageUploadDialog({ open, onClose, defaultCustomFilename }: Props) {
  const t = useTranslate();
  const notify = useNotify();
  const refresh = useRefresh();
  const fileRef = useRef<HTMLInputElement>(null);
  const [customFilename, setCustomFilename] = useState(defaultCustomFilename ?? '');
  const [loading, setLoading] = useState(false);

  const handleClose = () => {
    if (loading) return;
    onClose();
  };

  const handleUpload = async () => {
    const file = fileRef.current?.files?.[0];
    if (!file) {
      notify(t('module.coinImages.noFileSelected'), { type: 'warning' });
      return;
    }
    setLoading(true);
    try {
      await uploadCoinImage(file, customFilename || undefined);
      notify(t('module.coinImages.uploadSuccess'), { type: 'success' });
      refresh();
      onClose();
    } catch (e: any) {
      const msg =
        e?.response?.data?.message || e?.body?.message || e?.message || t('module.coinImages.uploadError');
      notify(msg, { type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t('module.coinImages.uploadFile')}</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
        <input
          ref={fileRef}
          type="file"
          accept="image/png,image/svg+xml,.png,.svg"
          style={{ display: 'none' }}
          onChange={() => {
            const f = fileRef.current?.files?.[0];
            if (f && !customFilename) {
              setCustomFilename(f.name.replace(/\.[^.]+$/, ''));
            }
          }}
        />
        <Button variant="outlined" disabled={loading} onClick={() => fileRef.current?.click()}>
          {t('module.coinImages.selectFile')}
        </Button>
        <TextField
          label={t('module.coinImages.customFilename')}
          value={customFilename}
          onChange={(e) => setCustomFilename(e.target.value)}
          helperText={t('module.coinImages.customFilenameHint')}
          disabled={loading}
          fullWidth
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          {t('actions.cancel')}
        </Button>
        <PermissionButton resource="cdn-coin-images" action="create">
          <Button variant="contained" onClick={handleUpload} disabled={loading}>
            {loading ? t('module.coinImages.uploading') : t('module.coinImages.upload')}
          </Button>
        </PermissionButton>
      </DialogActions>
    </Dialog>
  );
}
